import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldAlert, Eye, EyeOff, LogOut } from "lucide-react";

/**
 * Forced first-login password change. Blocks the entire app until the user
 * changes the auto-generated temp password. Triggered when the login
 * response has `user.force_password_reset === true`.
 */
export default function ForceChangePassword() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New password and confirmation do not match");
    if (next === current) return toast.error("New password must be different from the temporary password");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — welcome to Asset IQ");
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-700 mb-3">
            <ShieldAlert className="w-7 h-7" />
          </div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">First Login</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-1">Set your password</h1>
          <p className="text-slate-500 mt-2 text-sm">
            Hi {user?.name || "there"} — your account was created with a temporary password.
            <br />Please choose a permanent password to continue.
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={submit} className="space-y-4" data-testid="force-change-form">
              <div>
                <Label htmlFor="cur" className="text-xs uppercase tracking-wider text-slate-500">Temporary password</Label>
                <Input
                  id="cur"
                  type="password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  placeholder="The temp password given to you"
                  required
                  autoFocus
                  data-testid="force-current-input"
                />
              </div>

              <div>
                <Label htmlFor="new" className="text-xs uppercase tracking-wider text-slate-500">New password</Label>
                <div className="relative">
                  <Input
                    id="new"
                    type={showNext ? "text" : "password"}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    placeholder="Min 8 characters"
                    minLength={8}
                    required
                    className="pr-10"
                    data-testid="force-new-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNext((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrength value={next} />
              </div>

              <div>
                <Label htmlFor="conf" className="text-xs uppercase tracking-wider text-slate-500">Confirm new password</Label>
                <Input
                  id="conf"
                  type={showNext ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="force-confirm-input"
                />
                {confirm && next !== confirm && (
                  <div className="text-xs text-rose-600 mt-1">Passwords don&apos;t match</div>
                )}
              </div>

              <Button
                type="submit"
                disabled={submitting || !current || next.length < 8 || next !== confirm}
                className="w-full bg-[#7C1D3A] hover:bg-[#6c1c36]"
                data-testid="force-submit-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {submitting ? "Updating..." : "Set new password"}
              </Button>
            </form>

            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center">
              <button
                onClick={logout}
                className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                data-testid="force-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out instead
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PasswordStrength({ value }) {
  const rules = [
    { ok: value.length >= 8, text: "At least 8 characters" },
    { ok: /[A-Z]/.test(value) && /[a-z]/.test(value), text: "Mixed upper & lowercase" },
    { ok: /\d/.test(value), text: "At least one number" },
    { ok: /[^A-Za-z0-9]/.test(value), text: "At least one symbol (recommended)" },
  ];
  return (
    <ul className="mt-2 space-y-0.5 text-[11px]">
      {rules.map((r) => (
        <li key={r.text} className={r.ok ? "text-emerald-700" : "text-slate-500"}>
          <span className="inline-block w-3">{r.ok ? "✓" : "·"}</span> {r.text}
        </li>
      ))}
    </ul>
  );
}
